import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { uploadFileToStaging } from '@/integrations/supabase/driveUploadService';

export interface ShipmentPlanUploadInput {
  file: File;
  metadata: {
    plan_id?: string;
    report_name: string;
    report_date?: string;
    summary?: string;
    auth_user_id?: string;
    user_id?: string;
    created_by_user_id?: string;
  };
}

export interface ShipmentPlanUploadResponse {
  success: boolean;
  message?: string;
  data?: { plan_id: string; full_url: string; file_id: string };
  error?: string;
}

export function useShipmentPlanUpload() {
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState<'idle' | 'staging' | 'processing' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ file, metadata }: ShipmentPlanUploadInput): Promise<ShipmentPlanUploadResponse> => {
      setError(null);
      setProgress(0);
      setStage('staging');

      const stagingInterval = setInterval(() => {
        setProgress(prev => (prev < 60 ? prev + 5 : prev));
      }, 300);

      let storagePath: string;
      try {
        storagePath = await uploadFileToStaging(file);
      } finally {
        clearInterval(stagingInterval);
      }

      setProgress(65);
      setStage('processing');

      const edgeInterval = setInterval(() => {
        setProgress(prev => (prev < 93 ? prev + 2 : prev));
      }, 500);

      let result: ShipmentPlanUploadResponse;
      try {
        const { data, error } = await supabase.functions.invoke('upload-shipment-plan', {
          body: {
            storage_path: storagePath,
            file_name: file.name,
            file_type: file.type || 'text/html',
            ...metadata,
          },
        });
        if (error) {
          let realMessage = error.message || 'Edge Function call failed';
          try {
            const body = await (error as { context?: { json?: () => Promise<{ error?: string }> } }).context?.json?.();
            if (body?.error) realMessage = body.error;
          } catch (_) { /* ignore */ }
          throw new Error(realMessage);
        }
        result = data as ShipmentPlanUploadResponse;
      } finally {
        clearInterval(edgeInterval);
      }

      if (!result?.success) {
        throw new Error(result?.error || 'Upload failed');
      }

      setProgress(100);
      setStage('done');
      return result;
    },
    onSuccess: async () => {
      toast({ title: 'Tải báo cáo thành công', description: 'Shipment Plan đã được lưu.' });
      await queryClient.refetchQueries({ queryKey: ['shipment-plans'] });
      setTimeout(() => { setProgress(0); setStage('idle'); }, 1500);
    },
    onError: (err: Error) => {
      setError(err.message);
      setProgress(0);
      setStage('idle');
      toast({ title: 'Tải báo cáo thất bại', description: err.message, variant: 'destructive' });
    },
  });

  const reset = () => {
    setProgress(0);
    setStage('idle');
    setError(null);
    mutation.reset();
  };

  return {
    uploadShipmentPlan: mutation.mutateAsync,
    isUploading: mutation.isPending,
    progress,
    stage,
    error,
    reset,
  };
}
